import { useState } from 'react'
import { FiAlertTriangle, FiX, FiClock } from 'react-icons/fi'
import toast from 'react-hot-toast'
import api from '../../services/api'
import { Btn } from '../../ui'

/*
 * ModalAvisoCorreccionV2 — DS v2.
 * Se muestra cuando el backend devuelve un aviso de tipo `fuera_horario_*`
 * tras un fichaje. Ofrece al usuario solicitar una corrección con la hora
 * propuesta del calendario (editable).
 *
 * Props:
 *  - aviso: { tipo: 'fuera_horario_entrada'|'fuera_horario_salida', mensaje, hora_propuesta, hora_real, ventana }
 *  - fichaje: { id, fecha, hora_entrada, hora_salida }
 *  - onClose: () => void
 */
const ModalAvisoCorreccionV2 = ({ aviso, fichaje, onClose }) => {
  const esEntrada = aviso?.tipo === 'fuera_horario_entrada'
  const [hora, setHora] = useState(aviso?.hora_propuesta || aviso?.hora_real || '')
  const [motivo, setMotivo] = useState('')
  const [enviando, setEnviando] = useState(false)

  if (!aviso) return null

  const enviar = async () => {
    if (!motivo.trim()) { toast.error('Indica el motivo de la corrección'); return }
    if (!hora) { toast.error('La hora propuesta es obligatoria'); return }
    setEnviando(true)
    try {
      // Atajo de una sola operación sobre el nuevo flujo unificado (Punto C):
      // modifica únicamente la hora de entrada o de salida del fichaje afectado.
      const horaCanonica = hora.length === 5 ? `${hora}:00` : hora
      const operaciones = [{
        accion: 'modificar',
        objetivo: 'turno',
        marcaje: esEntrada ? 'entrada' : 'salida',
        fichaje_id: fichaje?.id,
        valores_propuestos: esEntrada ? { hora_entrada: horaCanonica } : { hora_salida: horaCanonica },
        orden: 0,
      }]
      await api.post('/correcciones/dia', { fecha: fichaje?.fecha, motivo, operaciones })
      toast.success('Solicitud enviada al administrador. Te avisaremos cuando se apruebe.', { duration: 6000 })
      onClose?.()
    } catch (e) {
      toast.error(e.message || 'No se pudo enviar la solicitud')
    } finally {
      setEnviando(false)
    }
  }

  const horaRealMostrada = aviso?.hora_real
    || (esEntrada ? fichaje?.hora_entrada : fichaje?.hora_salida)

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
      <div className="ui-card w-full max-w-md">
        <div className="flex items-start justify-between p-5 border-b border-[var(--line)]">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-soft bg-[rgba(168,106,6,.10)] flex items-center justify-center">
              <FiAlertTriangle size={15} className="text-[#A86A06]" />
            </div>
            <div>
              <div className="eyebrow mb-1">Solicitar corrección</div>
              <h3 className="font-display text-[20px] text-[var(--ink)] leading-tight">
                {esEntrada ? 'Entrada fuera del horario habitual' : 'Salida fuera del horario habitual'}.
              </h3>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-soft text-[var(--ink-3)] hover:text-[var(--ink)] hover:bg-[var(--bg-2)]"
            aria-label="Cerrar"
            disabled={enviando}
          >
            <FiX size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-[13px] text-[var(--ink-2)]">{aviso.mensaje}</p>

          {/* Aviso explícito del flujo: el fichaje queda como está hasta aprobación. */}
          <div className="rounded-soft bg-[rgba(168,106,6,.08)] border border-[rgba(168,106,6,.20)] p-3 text-[12px] text-[var(--ink-2)] leading-relaxed">
            <strong className="text-[var(--ink)]">Importante.</strong>{' '}
            Tu fichaje seguirá registrado a las{' '}
            <b className="font-mono tnum">{horaRealMostrada || '—'}</b>{' '}
            hasta que un administrador apruebe esta solicitud. Si la rechaza, te avisaremos por email.
          </div>

          {aviso.ventana && (
            <div className="rounded-soft bg-[var(--bg-2)] p-3 text-[12px] text-[var(--ink-2)] flex items-center gap-2">
              <FiClock size={13} />
              <span>Ventana permitida: <b>{aviso.ventana.min}</b> – <b>{aviso.ventana.max}</b></span>
            </div>
          )}

          <div>
            <label className="eyebrow block mb-1.5">Hora correcta *</label>
            <input
              type="time"
              value={hora}
              onChange={(e) => setHora(e.target.value)}
              className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] focus:outline-none focus:border-[var(--ink-3)]"
              required
            />
            {aviso.hora_propuesta && (
              <p className="text-[11px] text-[var(--ink-3)] mt-1">
                Sugerida: {aviso.hora_propuesta} (extremo válido del calendario). Puedes editarla.
              </p>
            )}
          </div>

          <div>
            <label className="eyebrow block mb-1.5">Motivo *</label>
            <textarea
              rows={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej: olvidé fichar al entrar, llegué a la hora pero..."
              className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] focus:outline-none focus:border-[var(--ink-3)] resize-none"
              required
            />
          </div>

          <div className="flex gap-3 pt-1">
            <Btn type="button" variant="ghost" className="flex-1" onClick={onClose} disabled={enviando}>
              Dejar como está
            </Btn>
            <Btn type="button" variant="primary" className="flex-1" onClick={enviar} disabled={enviando}>
              {enviando ? 'Enviando…' : 'Enviar solicitud'}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModalAvisoCorreccionV2
